'use client';
import { useState } from 'react';

import { EmptyState, ErrorState, SportsSidebarItem } from '@/components';
import { SidebarItem, SportsSidebarCategory } from '@/types';

interface SportsSidebarListProps {
  /** Indicates if sidebar data is still loading */
  isLoading: boolean;
  /** Indicates if there was an error fetching sidebar data */
  isError: boolean;
  /** List of sports categories to display */
  items: SportsSidebarCategory[];
  /** The ID of the currently active sport or league (from URL) */
  activeId?: string | number | null;
  /** Callback to handle navigation when a category or league is clicked */
  onCategoryClick: (item: SidebarItem) => void;
  /** Callback to retry data fetching on error */
  onRetry: () => void;
}

/**
 * SportsSidebarList renders the collection of sports categories.
 * It manages the accordion state to ensure only one category is open at a time.
 */
export const SportsSidebarList = ({
  isLoading,
  isError,
  items,
  activeId,
  onCategoryClick,
  onRetry,
}: SportsSidebarListProps) => {
  // Tracks which category is currently expanded.
  const [expandedId, setExpandedId] = useState<string | number | null>(null);

  // Sync expanded state whenever the activeId (URL parameter) changes
  // We use the "derived state" pattern to avoid the useEffect setState warning
  const [prevActiveId, setPrevActiveId] = useState(activeId);

  if (activeId !== prevActiveId) {
    setPrevActiveId(activeId);

    // Find if the currently active item (league or category) belongs to any category
    const activeCategory = items.find((item) => {
      if (activeId === item.category) return true;
      if (item.type === 'automated') {
        return item.leagues.some((l) => String(l.id) === String(activeId));
      }
      return false;
    });

    if (activeCategory) {
      setExpandedId(activeCategory.category);
    }
  }

  /**
   * Toggles the expansion state of a category.
   * If the clicked category is already open, it collapses; otherwise, it opens and closes others.
   */
  const handleToggle = (categoryId: string | number) => {
    setExpandedId((prev) => (prev === categoryId ? null : categoryId));
  };

  if (isError) {
    return <ErrorState compact title="Error" message="Failed to load sports" onRetry={onRetry} />;
  }

  if (items.length === 0 && !isLoading) {
    return (
      <EmptyState
        title="No sports found"
        description="Try a different category or wait for live events."
      />
    );
  }

  /**
   * Helper to render a single category item.
   * Passed as a reference to .map() to avoid arrow functions in JSX.
   */
  const renderCategoryItem = (item: SportsSidebarCategory, index: number) => {
    // A category is active if its name matches the activeId OR if one of its leagues matches the activeId
    const isCatActive =
      activeId === item.category ||
      (item.type === 'automated' && item.leagues.some((l) => String(l.id) === String(activeId)));

    const isExpanded = expandedId === item.category;

    return (
      <SportsSidebarItem
        key={`${item.category}-${index}`}
        category={item}
        isActive={isCatActive}
        isExpanded={isExpanded}
        activeId={activeId}
        onToggle={handleToggle}
        onClick={onCategoryClick}
      />
    );
  };

  const renderedCategoryItems = items.map(renderCategoryItem);

  return <>{renderedCategoryItems}</>;
};
